"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.asArray = asArray;
exports.asString = asString;
exports.fromString = void 0;
exports.isStringColor = isStringColor;
exports.normalize = normalize;
exports.toString = toString;

var _asserts = require("./asserts.js");

var _math = require("./math.js");

/**
 * @module ol/color
 */

/**
 * A color represented as a short array [red, green, blue, alpha].
 * red, green, and blue should be integers in the range 0..255 inclusive.
 * alpha should be a float in the range 0..1 inclusive. If no alpha value is
 * given then `1` will be used.
 * @typedef {Array<number>} Color
 * @api
 */

/**
 * This RegExp matches # followed by 3, 4, 6, or 8 hex digits.
 * @const
 * @type {RegExp}
 * @private
 */
var HEX_COLOR_RE_ = /^#([a-f0-9]{3}|[a-f0-9]{4}(?:[a-f0-9]{2}){0,2})$/i;
/**
 * Regular expression for matching potential named color style strings.
 * @const
 * @type {RegExp}
 * @private
 */

var NAMED_COLOR_RE_ = /^([a-z]*)$|^hsla?\(.*\)$/i;
/**
 * Return the color as an rgba string.
 * @param {Color|string} color Color.
 * @return {string} Rgba string.
 * @api
 */

function asString(color) {
  if (typeof color === 'string') {
    return color;
  } else {
    return toString(color);
  }
}
/**
 * Return named color as an rgba string.
 * @param {string} color Named color.
 * @return {string} Rgb string.
 */


function fromNamed(color) {
  var el = document.createElement('div');
  el.style.color = color;

  if (el.style.color !== '') {
    document.body.appendChild(el);
    var rgb = getComputedStyle(el).color;
    document.body.removeChild(el);
    return rgb;
  } else {
    return '';
  }
}
/**
 * @param {string} s String.
 * @return {Color} Color.
 */


var fromString = function () {
  // We maintain a small cache of parsed strings.  To provide cheap LRU-like
  // semantics, whenever the cache grows too large we simply delete an
  // arbitrary 25% of the entries.

  /**
   * @const
   * @type {number}
   */
  var MAX_CACHE_SIZE = 1024;
  /**
   * @type {Object<string, Color>}
   */

  var cache = {};
  /**
   * @type {number}
   */

  var cacheSize = 0;
  return (
    /**
     * @param {string} s String.
     * @return {Color} Color.
     */
    function (s) {
      var color;

      if (cache.hasOwnProperty(s)) {
        color = cache[s];
      } else {
        if (cacheSize >= MAX_CACHE_SIZE) {
          var i = 0;

          for (var key in cache) {
            if ((i++ & 3) === 0) {
              delete cache[key];
              --cacheSize;
            }
          }
        }

        color = fromStringInternal_(s);
        cache[s] = color;
        ++cacheSize;
      }

      return color;
    }
  );
}();
/**
 * Return the color as an array. This function maintains a cache of calculated
 * arrays which means the result should not be modified.
 * @param {Color|string} color Color.
 * @return {Color} Color.
 * @api
 */


exports.fromString = fromString;

function asArray(color) {
  if (Array.isArray(color)) {
    return color;
  } else {
    return fromString(color);
  }
}
/**
 * @param {string} s String.
 * @private
 * @return {Color} Color.
 */


function fromStringInternal_(s) {
  var r, g, b, a, color;

  if (NAMED_COLOR_RE_.exec(s)) {
    s = fromNamed(s);
  }

  if (HEX_COLOR_RE_.exec(s)) {
    // hex
    var n = s.length - 1; // number of hex digits

    var d = // number of digits per channel
    void 0; // number of digits per channel

    if (n <= 4) {
      d = 1;
    } else {
      d = 2;
    }

    var hasAlpha = n === 4 || n === 8;
    r = parseInt(s.substr(1 + 0 * d, d), 16);
    g = parseInt(s.substr(1 + 1 * d, d), 16);
    b = parseInt(s.substr(1 + 2 * d, d), 16);

    if (hasAlpha) {
      a = parseInt(s.substr(1 + 3 * d, d), 16);
    } else {
      a = 255;
    }

    if (d == 1) {
      r = (r << 4) + r;
      g = (g << 4) + g;
      b = (b << 4) + b;

      if (hasAlpha) {
        a = (a << 4) + a;
      }
    }

    color = [r, g, b, a / 255];
  } else if (s.indexOf('rgba(') == 0) {
    // rgba()
    color = s.slice(5, -1).split(',').map(Number);
    normalize(color);
  } else if (s.indexOf('rgb(') == 0) {
    // rgb()
    color = s.slice(4, -1).split(',').map(Number);
    color.push(1);
    normalize(color);
  } else {
    (0, _asserts.assert)(false, 14); // Invalid color
  }

  return color;
}
/**
 * TODO this function is only used in the test, we probably shouldn't export it
 * @param {Color} color Color.
 * @return {Color} Clamped color.
 */


function normalize(color) {
  color[0] = (0, _math.clamp)(color[0] + 0.5 | 0, 0, 255);
  color[1] = (0, _math.clamp)(color[1] + 0.5 | 0, 0, 255);
  color[2] = (0, _math.clamp)(color[2] + 0.5 | 0, 0, 255);
  color[3] = (0, _math.clamp)(color[3], 0, 1);
  return color;
}
/**
 * @param {Color} color Color.
 * @return {string} String.
 */


function toString(color) {
  var r = color[0];

  if (r != (r | 0)) {
    r = r + 0.5 | 0;
  }

  var g = color[1];

  if (g != (g | 0)) {
    g = g + 0.5 | 0;
  }

  var b = color[2];

  if (b != (b | 0)) {
    b = b + 0.5 | 0;
  }

  var a = color[3] === undefined ? 1 : Math.round(color[3] * 100) / 100;
  return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
}
/**
 * @param {string} s String.
 * @return {boolean} Whether the string is actually a valid color
 */


function isStringColor(s) {
  if (NAMED_COLOR_RE_.test(s)) {
    s = fromNamed(s);
  }

  return HEX_COLOR_RE_.test(s) || s.indexOf('rgba(') === 0 || s.indexOf('rgb(') === 0;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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